import React, { useState } from 'react';
import { 
  TrendingUp, DollarSign, Music, Users, Play, Download, 
  Calendar, Filter, RefreshCw 
} from 'lucide-react';
import { Card } from '../components/common/Card';
import { Button } from '../components/common/Button';
import { Select } from '../components/common/Select';
import { MetricCard } from '../components/analytics/MetricCard';
import { RevenueChart } from '../components/analytics/RevenueChart';
import { TopSongsChart } from '../components/analytics/TopSongsChart';
import { GeographicChart } from '../components/analytics/GeographicChart';
import { StorePerformanceChart } from '../components/analytics/StorePerformanceChart';
import { useToast } from '../components/common/Toast';
import { mockSongs, mockArtists, mockAnalytics } from '../data/mockData';

export const Analytics: React.FC = () => {
  const [timePeriod, setTimePeriod] = useState<'7d' | '30d' | '90d' | '1y'>('30d');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { addToast } = useToast();

  const periodOptions = [
    { value: '7d', label: 'Last 7 days' },
    { value: '30d', label: 'Last 30 days' },
    { value: '90d', label: 'Last 90 days' },
    { value: '1y', label: 'Last year' }
  ];

  // Generate mock analytics data based on selected period
  const generateAnalyticsData = (period: string) => {
    const days = period === '7d' ? 7 : period === '30d' ? 30 : period === '90d' ? 90 : 365;
    const data = [];
    
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      
      data.push({
        date: date.toISOString().split('T')[0],
        revenue: Math.floor(Math.random() * 1000) + 500,
        streams: Math.floor(Math.random() * 50000) + 25000
      });
    }
    
    return data;
  };

  const analyticsData = generateAnalyticsData(timePeriod);
  
  // Calculate totals
  const totalRevenue = mockSongs.reduce((sum, song) => sum + song.revenue, 0);
  const totalStreams = mockSongs.reduce((sum, song) => sum + song.streams, 0);
  const totalSongs = mockSongs.length;
  const totalArtists = mockArtists.length;

  // Mock geographic data
  const geographicData = [
    { country: 'India', streams: 4500000, revenue: 13500 },
    { country: 'United States', streams: 2200000, revenue: 15400 },
    { country: 'United Kingdom', streams: 800000, revenue: 5600 },
    { country: 'Canada', streams: 650000, revenue: 4550 },
    { country: 'Australia', streams: 420000, revenue: 2940 },
    { country: 'Germany', streams: 380000, revenue: 2660 },
    { country: 'Brazil', streams: 320000, revenue: 1280 }
  ];

  const handleRefresh = () => {
    setIsRefreshing(true);
    addToast({
      type: 'info',
      title: 'Refreshing Analytics',
      message: 'Updating analytics data from all platforms...'
    });
    
    setTimeout(() => {
      setIsRefreshing(false);
      addToast({
        type: 'success',
        title: 'Analytics Updated',
        message: 'Your analytics data has been refreshed successfully.'
      });
    }, 2000);
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const getChangeData = (baseValue: number) => ({
    value: Math.floor(Math.random() * 30) + 5,
    type: Math.random() > 0.3 ? 'increase' : 'decrease' as 'increase' | 'decrease',
    period: timePeriod === '7d' ? 'vs last week' : timePeriod === '30d' ? 'vs last month' : 'vs last period'
  });

  return (
    <div className="space-y-6">
      {/* Header Controls */}
      <Card className="p-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-white font-display mb-1">
              Analytics Overview
            </h2>
            <p className="text-gray-400">
              Track your music performance across all platforms
            </p>
          </div>
          <div className="flex items-center gap-4">
            <Select
              value={timePeriod}
              onChange={(value) => setTimePeriod(value as any)}
              options={periodOptions}
              className="min-w-40"
            />
            <Button
              variant="outline"
              onClick={handleRefresh}
              disabled={isRefreshing}
              icon={RefreshCw}
              className={isRefreshing ? 'animate-spin' : ''}
            >
              {isRefreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
          </div>
        </div>
      </Card>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Total Revenue"
          value={totalRevenue}
          prefix="$"
          icon={DollarSign}
          change={getChangeData(totalRevenue)}
          color="gold"
        />
        <MetricCard
          title="Total Streams"
          value={formatNumber(totalStreams)}
          icon={Play}
          change={getChangeData(totalStreams)}
          color="green"
        />
        <MetricCard
          title="Distributed Songs"
          value={totalSongs}
          icon={Music}
          change={getChangeData(totalSongs)}
          color="blue"
        />
        <MetricCard
          title="Active Artists"
          value={totalArtists}
          icon={Users}
          change={getChangeData(totalArtists)}
          color="purple"
        />
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="xl:col-span-2">
          <RevenueChart data={analyticsData} period={timePeriod} />
        </div>
        
        <TopSongsChart />
        <GeographicChart data={geographicData} />
        
        <div className="xl:col-span-2">
          <StorePerformanceChart />
        </div>
      </div>

      {/* Additional Insights */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <h3 className="text-lg font-semibold text-white font-display mb-4">
            Recent Achievements
          </h3>
          <div className="space-y-3">
            <div className="flex items-center space-x-3 p-3 bg-green-500/10 rounded-lg border border-green-500/20">
              <TrendingUp className="w-5 h-5 text-green-400" />
              <div>
                <p className="text-sm font-medium text-white">1M+ Streams</p>
                <p className="text-xs text-gray-400">"Bollywood Nights" milestone</p>
              </div>
            </div>
            <div className="flex items-center space-x-3 p-3 bg-gold-500/10 rounded-lg border border-gold-500/20">
              <DollarSign className="w-5 h-5 text-gold-400" />
              <div>
                <p className="text-sm font-medium text-white">$10K+ Revenue</p>
                <p className="text-xs text-gray-400">Monthly milestone reached</p>
              </div>
            </div>
            <div className="flex items-center space-x-3 p-3 bg-blue-500/10 rounded-lg border border-blue-500/20">
              <Music className="w-5 h-5 text-blue-400" />
              <div>
                <p className="text-sm font-medium text-white">50+ Songs</p>
                <p className="text-xs text-gray-400">Catalog milestone</p>
              </div>
            </div>
          </div>
        </Card>

        <Card>
          <h3 className="text-lg font-semibold text-white font-display mb-4">
            Performance Insights
          </h3>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-400">Avg. Revenue per Stream</span>
                <span className="text-white font-medium">$0.0041</span>
              </div>
              <div className="w-full bg-gray-700 rounded-full h-2">
                <div className="bg-gold-400 h-2 rounded-full" style={{ width: '68%' }} />
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-400">Platform Conversion</span>
                <span className="text-white font-medium">73%</span>
              </div>
              <div className="w-full bg-gray-700 rounded-full h-2">
                <div className="bg-green-400 h-2 rounded-full" style={{ width: '73%' }} />
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-400">Artist Engagement</span>
                <span className="text-white font-medium">85%</span>
              </div>
              <div className="w-full bg-gray-700 rounded-full h-2">
                <div className="bg-blue-400 h-2 rounded-full" style={{ width: '85%' }} />
              </div>
            </div>
          </div>
        </Card>

        <Card>
          <h3 className="text-lg font-semibold text-white font-display mb-4">
            Quick Actions
          </h3>
          <div className="space-y-3">
            <Button className="w-full" size="sm" icon={Download}>
              Export Analytics Report
            </Button>
            <Button variant="outline" className="w-full" size="sm" icon={Calendar}>
              Schedule Report
            </Button>
            <Button variant="outline" className="w-full" size="sm" icon={Filter}>
              Custom Date Range
            </Button>
          </div>
          
          <div className="mt-6 pt-4 border-t border-gray-700">
            <p className="text-xs text-gray-400 mb-2">Last updated</p>
            <p className="text-sm text-white">
              {new Date().toLocaleString()}
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
};